import { useCallback, useSyncExternalStore } from "react";
import { useIsDarkMode } from "~/hooks/useIsDarkMode";
import { readStoredPreferences, useUserPreferences } from "~/hooks/useUserPreferences";

export type FigureTheme = "light" | "dark";

/** What the user picked, as opposed to what figures resolve to. `"app"` is the
 *  unset state, where figures follow the app theme. */
export type FigureThemeChoice = FigureTheme | "app";

// `useLocalStorage` never syncs same-tab writes, so writers broadcast here and
// every mounted figure re-reads — otherwise sibling charts would hold the old
// theme until something remounted them.
const FIGURE_THEME_EVENT = "sc-figure-theme-change";

const subscribe = (onStoreChange: () => void) => {
  window.addEventListener(FIGURE_THEME_EVENT, onStoreChange);
  return () => window.removeEventListener(FIGURE_THEME_EVENT, onStoreChange);
};

const readPinnedTheme = (): FigureTheme | undefined => readStoredPreferences().figureTheme;

export interface FigureThemeControl {
  /** The theme figures render in right now. */
  figureTheme: FigureTheme;
  figureThemeChoice: FigureThemeChoice;
  setFigureThemeChoice: (choice: FigureThemeChoice) => void;
  /** Pins the opposite of the theme in effect. */
  toggleFigureTheme: () => void;
}

/**
 * The theme figures render in, and the two ways it changes: a figure's modebar
 * toggle, or the Appearance tab's dropdown.
 *
 * Unpinned, figures follow the app theme. Pinning one stops them following;
 * `"app"` puts them back. The modebar toggle only ever pins, since a two-state
 * button has nowhere to put a third choice.
 */
export const useFigureTheme = (): FigureThemeControl => {
  const [, updatePreferences] = useUserPreferences();
  const isAppDark = useIsDarkMode();
  const pinnedTheme = useSyncExternalStore(subscribe, readPinnedTheme);
  const figureTheme: FigureTheme = pinnedTheme ?? (isAppDark ? "dark" : "light");

  const setFigureThemeChoice = useCallback(
    (choice: FigureThemeChoice) => {
      // `"app"` clears the key rather than storing a third value, so "never
      // chose" and "chose to follow" stay one state. `undefined` does clear it:
      // the preferences blob is JSON, which drops undefined members.
      updatePreferences({ figureTheme: choice === "app" ? undefined : choice });
      window.dispatchEvent(new Event(FIGURE_THEME_EVENT));
    },
    [updatePreferences]
  );

  const toggleFigureTheme = useCallback(
    () => setFigureThemeChoice(figureTheme === "dark" ? "light" : "dark"),
    [figureTheme, setFigureThemeChoice]
  );

  return {
    figureTheme,
    figureThemeChoice: pinnedTheme ?? "app",
    setFigureThemeChoice,
    toggleFigureTheme,
  };
};
